import React from "react";
import { Link } from "react-router-dom";

/**
 * Cluster article — "Por qué abandono siempre".
 * Category: preparation. Psychological angle.
 */

function Section({ title, children, id }) {
  return (
    <section className="space-y-4" id={id}>
      <h2 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mt-12 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Pullquote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#B4C5A4] pl-5 py-1 my-6 font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug italic">
      {children}
    </blockquote>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CATALÀ
// ════════════════════════════════════════════════════════════════════════════
export function PerQueAbandonoCA() {
  return (
    <>
      <p>
        {"Has començat cinc vegades. O deu. Cada un dels intents ha durat entre una setmana i dos mesos. Cada un ha acabat igual: un dia el saltes, una setmana ho deixes, i llavors ja no tornes fins que passa alguna cosa que et fa començar de nou. Els altres et diuen que ets tu, que no tens voluntat, que no ho vols prou. I al final tu també t'ho creus."}
      </p>
      <p>
        {"Aquest article va sobre per què no és voluntat. I sobre com trencar el patró aquesta vegada — no perquè aquesta sigui la darrera, sinó perquè no en necessitis una darrera."}
      </p>

      <Section title="El mite de la voluntat" id="mite">
        <p>
          {"La voluntat no és una qualitat que tens o no tens. És un recurs limitat, com la bateria del mòbil. Cada decisió del dia (què esmorzo, com em vesteixo, què responc a un email difícil, si li dic que no a un cafè) consumeix una mica. Al final del dia, quan toca decidir 'em moc o miro la tele', tens la bateria al 5%. I la voluntat sola perd."}
        </p>
        <p>
          {"La gent que se sosté en un canvi durant anys no té més voluntat que tu. Té millor sistema. Ha convertit les decisions en rutina, i les rutines no consumeixen bateria. La diferència és arquitectura, no caràcter."}
        </p>
        <Pullquote>
          {"No abandones perquè siguis feble. Abandones perquè el mètode et demana voluntat cada dia."}
        </Pullquote>
      </Section>

      <Section title="Els 3 punts on gairebé sempre s'abandona" id="punts">
        <p>
          {"Si mires enrere els teus intents, veuràs que abandones gairebé sempre en els mateixos moments. Coneixe'ls et canvia el joc:"}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Setmana 2: el moment 'l'entusiasme s'ha acabat'."}</strong>{" "}
          {"Els primers dies estàs motivat, l'endorfina del canvi et sosté. Cap al final de la primera setmana ja no hi ha novetat. Ara és feina. I aquí molts abandonen perquè creien que la motivació sostindria tot el projecte. No ho fa."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Setmana 4-6: el moment 'no veig resultats'."}</strong>{" "}
          {"Has fet un mes. La bàscula no mou el que esperaves. Ningú et diu 'com t'has posat!'. Sembla que has invertit temps per res. Aquí molts abandonen perquè el cervell demana feedback constant i el cos no en dona en aquest ritme."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Setmana 8-10: el moment 'una cosa gran passa'."}</strong>{" "}
          {"Un viatge, una crisi familiar, una malaltia, la feina et menja les tardes. Un moment on ja no pots mantenir la rutina. Aquí molts abandonen perquè creuen que si tallen 5 dies ja s'ha trencat tot. No és així — però la mentalitat 'tot o res' fa que sigui així."}
        </p>
      </Section>

      <Section title="Per què el mètode del 'tot o res' et destrossa" id="tot-o-res">
        <p>
          {"El pensament 'tot o res' és el pitjor enemic d'un canvi durador. Va així: 'aquesta setmana faig cinc dies de moviment. Si en fallo un, ja no compta i comença de nou dilluns'. Amb aquesta lògica, un dia malament esborra una setmana bona. I com que sempre tindràs dies dolents, sempre acabaràs a zero."}
        </p>
        <p>
          {"L'antídot és una idea aparentment senzilla però radical: no hi ha 'tot o res'. Hi ha suma. Si havies de fer 5 minuts i n'has fet 2, has fet 2 minuts. Si havies de moure't 4 dies i només 2, n'has fet 2. Si no has fet res una setmana, tornes a fer 2 minuts la següent. La bàscula del canvi és cumulativa, no reset."}
        </p>
      </Section>

      <Section title="El sistema que evita que abandonis" id="sistema">
        <p>
          {"Un canvi durador no és 'tenir més voluntat'. És eliminar les decisions que consumeixen voluntat. Tres pilars:"}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"1. Petit fins que sembli tonto."}</strong>{" "}
          {"Si el compromís és 'faré 30 minuts d'exercici diaris', la voluntat el trencarà. Si el compromís és 'em posaré les sabatilles i faré 3 respiracions fondes cada matí', costa molt trencar-ho — perquè no demana voluntat, demana 30 segons. Un cop tens les sabatilles posades, moltes vegades caminaràs. Però encara que no ho facis, has complert el compromís."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"2. Vinculat a alguna cosa que ja fas."}</strong>{" "}
          {"No has de crear un moment nou al dia. Has d'enganxar el moviment a un moment que ja existeix. 'Després del cafè del matí, 3 respiracions fondes'. 'Abans de dutxar-me a la nit, empenyer la paret 5 vegades'. La rutina antiga arrossega la nova."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"3. Sense càstig quan falles."}</strong>{" "}
          {"El càstig et desconnecta. Si un dia fallas i llavors et dius 'sóc un desastre', la teva relació amb el projecte es contamina. La regla d'or és: si falles un dia, torna l'endemà. Si falles una setmana, torna la següent, encara més petit. Mai 'compenso' — sempre 'torno'."}
        </p>
      </Section>

      <Section title="Què fer quan sents que tornes a abandonar" id="tornar">
        <p>
          {"Un dia arribarà. Notaràs que fa 3 dies que et saltes els 5 minuts. La veu vella tornarà: 'ja hi som una altra vegada, no ho aconseguiré mai'. Aquest és el moment crític. La majoria d'intents es perden aquí — no per no fer-ho, sinó per la interpretació d'aquest fet."}
        </p>
        <p>
          {"El que necessites llavors no és més voluntat. Necessites un protocol de recuperació. Aquí en tens un simple:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Reconèixer sense drama: 'fa X dies que no faig'."}</li>
          <li>{"Investigar sense càstig: 'què ha passat aquesta setmana?' (viatge, insomni, estrès?)"}</li>
          <li>{"Baixar el llistó: la propera acció ha de ser un 30% del que havies fet fins ara."}</li>
          <li>{"Tornar demà. No la setmana que ve. Demà. Amb la versió petita."}</li>
        </ul>
        <p>
          {"Aquest protocol funciona perquè treu el moralisme de l'equació. No estàs sent 'fort' o 'feble' — estàs gestionant un projecte a llarg termini que té trams bons i trams dolents. Els trams dolents formen part del projecte, no en són el final."}
        </p>
      </Section>

      <Section title="La pregunta que canvia tot" id="pregunta">
        <p>
          {"Molts intents fallen perquè estan connectats a una data. 'Vull perdre pes abans de l'estiu'. 'Vull estar millor per Nadal'. Aquestes fites tenen un problema: quan arriben (o quan és evident que no arribaràs), el projecte deixa de tenir sentit."}
        </p>
        <p>
          {"La pregunta que canvia el joc és una altra. No és 'què vull aconseguir?'. És 'qui vull ser cada dia?'. La resposta és més senzilla del que sembla. No 'vull ser prim'. Sinó 'vull ser algú que camina cada dia una mica'. No 'vull tenir un cos de gimnàs'. Sinó 'vull ser algú que respon millor al que li demana la vida'. Aquesta identitat no té data d'expiració."}
        </p>
        <Pullquote>
          {"Un objectiu s'acaba. Una identitat es cuida cada dia."}
        </Pullquote>
      </Section>

      <Section title="Un pas concret que pots fer avui" id="pas-avui">
        <p>
          {"Escriu en un paper el compromís més petit que se t'acut. Ha de ser tan petit que et faci vergonya llegir-lo en veu alta. Alguna cosa com: 'em posaré les sabatilles esportives dilluns al matí'. Res més. No 'i llavors sortiré a caminar'. Només posar-se les sabatilles."}
        </p>
        <p>
          {"Fes això una setmana. Cinc dies feiners, posar-te les sabatilles al matí. No cal caminar. Si camines, bé. Si no, també bé. Al cap de la setmana, revisa: quants dies te les has posat?"}
        </p>
        <p>
          {"Aquest tipus de compromís mínim s'aplica especialment bé quan comences a moure't per primera vegada — hi ha una guia sencera sobre "}
          <Link to="/blog/com-comencar-a-moure-amb-sobrepes" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            {"com començar a moure't si tens sobrepès"}
          </Link>
          {", amb el mètode dels 5 minuts, els tres exercicis de força bàsics i com escoltar el dolor sense abandonar-ho tot."}
        </p>
        <p>
          {"I si un dels motius que et fa abandonar és sentir-te observat, "}
          <Link to="/blog/em-fa-vergonya-anar-al-gimnas" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            {"en aquest article expliquem per què fa vergonya moure's en públic"}
          </Link>
          {" i quins espais funcionen millor mentre construeixes confiança."}
        </p>
        <p>
          {"Si vols entendre la relació entre aquest tipus de compromís i el veritable canvi de fons, pots llegir la nostra guia central sobre "}
          <Link to="/blog/com-activar-te-quan-fa-anys-que-no-et-mous" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            com activar-te quan fa anys que no et mous
          </Link>
          {". Allà expliquem per què començar petit és el 90% del canvi i com construir una identitat sostenible."}
        </p>
        <p>
          {"I si vols que t'ajudem a mapejar quins són els teus 3 punts d'abandonament habituals, deixa'ns el teu correu i et farem arribar un test inicial per identificar-los junts."}
        </p>
      </Section>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CASTELLANO
// ════════════════════════════════════════════════════════════════════════════
export function PorQueAbandonoES() {
  return (
    <>
      <p>
        {"Has empezado cinco veces. O diez. Cada uno de los intentos ha durado entre una semana y dos meses. Cada uno ha acabado igual: un día te lo saltas, una semana lo dejas, y entonces ya no vuelves hasta que pasa algo que te hace empezar de nuevo. Los demás te dicen que eres tú, que no tienes voluntad, que no lo quieres lo suficiente. Y al final tú también te lo crees."}
      </p>
      <p>
        {"Este artículo va sobre por qué no es voluntad. Y sobre cómo romper el patrón esta vez — no porque esta sea la última, sino para que no necesites una última."}
      </p>

      <Section title="El mito de la voluntad" id="mito">
        <p>
          {"La voluntad no es una cualidad que tienes o no tienes. Es un recurso limitado, como la batería del móvil. Cada decisión del día (qué desayuno, cómo me visto, qué respondo a un email difícil, si le digo que no a un café) consume un poco. Al final del día, cuando toca decidir 'me muevo o miro la tele', tienes la batería al 5%. Y la voluntad sola pierde."}
        </p>
        <p>
          {"La gente que se sostiene en un cambio durante años no tiene más voluntad que tú. Tiene mejor sistema. Ha convertido las decisiones en rutina, y las rutinas no consumen batería. La diferencia es arquitectura, no carácter."}
        </p>
        <Pullquote>
          {"No abandonas porque seas débil. Abandonas porque el método te pide voluntad cada día."}
        </Pullquote>
      </Section>

      <Section title="Los 3 puntos donde casi siempre se abandona" id="puntos">
        <p>
          {"Si miras atrás tus intentos, verás que abandonas casi siempre en los mismos momentos. Conocerlos te cambia el juego:"}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Semana 2: el momento 'el entusiasmo se ha acabado'."}</strong>{" "}
          {"Los primeros días estás motivado, la endorfina del cambio te sostiene. Hacia el final de la primera semana ya no hay novedad. Ahora es trabajo. Y aquí muchos abandonan porque creían que la motivación sostendría todo el proyecto. No lo hace."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Semana 4-6: el momento 'no veo resultados'."}</strong>{" "}
          {"Has hecho un mes. La báscula no se mueve lo que esperabas. Nadie te dice '¡cómo te has puesto!'. Parece que has invertido tiempo por nada. Aquí muchos abandonan porque el cerebro pide feedback constante y el cuerpo no lo da a este ritmo."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"Semana 8-10: el momento 'una cosa grande pasa'."}</strong>{" "}
          {"Un viaje, una crisis familiar, una enfermedad, el trabajo te come las tardes. Un momento donde ya no puedes mantener la rutina. Aquí muchos abandonan porque creen que si cortan 5 días ya se ha roto todo. No es así — pero la mentalidad 'todo o nada' hace que sea así."}
        </p>
      </Section>

      <Section title="Por qué el método del 'todo o nada' te destroza" id="todo-o-nada">
        <p>
          {"El pensamiento 'todo o nada' es el peor enemigo de un cambio duradero. Va así: 'esta semana hago cinco días de movimiento. Si fallo uno, ya no cuenta y empieza de nuevo el lunes'. Con esta lógica, un día malo borra una semana buena. Y como siempre tendrás días malos, siempre acabarás a cero."}
        </p>
        <p>
          {"El antídoto es una idea aparentemente sencilla pero radical: no hay 'todo o nada'. Hay suma. Si tenías que hacer 5 minutos y has hecho 2, has hecho 2 minutos. Si tenías que moverte 4 días y solo 2, has hecho 2. Si no has hecho nada una semana, vuelves a hacer 2 minutos la siguiente. La báscula del cambio es acumulativa, no reset."}
        </p>
      </Section>

      <Section title="El sistema que evita que abandones" id="sistema">
        <p>
          {"Un cambio duradero no es 'tener más voluntad'. Es eliminar las decisiones que consumen voluntad. Tres pilares:"}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"1. Pequeño hasta que parezca tonto."}</strong>{" "}
          {"Si el compromiso es 'haré 30 minutos de ejercicio diarios', la voluntad lo romperá. Si el compromiso es 'me pondré las zapatillas y haré 3 respiraciones profundas cada mañana', cuesta mucho romperlo — porque no pide voluntad, pide 30 segundos. Una vez tienes las zapatillas puestas, muchas veces caminarás. Pero aunque no lo hagas, has cumplido el compromiso."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"2. Vinculado a algo que ya haces."}</strong>{" "}
          {"No tienes que crear un momento nuevo en el día. Tienes que enganchar el movimiento a un momento que ya existe. 'Después del café de la mañana, 3 respiraciones profundas'. 'Antes de ducharme por la noche, empujar la pared 5 veces'. La rutina antigua arrastra la nueva."}
        </p>
        <p>
          <strong className="text-[#0B2545]">{"3. Sin castigo cuando fallas."}</strong>{" "}
          {"El castigo te desconecta. Si un día fallas y entonces te dices 'soy un desastre', tu relación con el proyecto se contamina. La regla de oro es: si fallas un día, vuelve al siguiente. Si fallas una semana, vuelve la siguiente, aún más pequeño. Nunca 'compenso' — siempre 'vuelvo'."}
        </p>
      </Section>

      <Section title="Qué hacer cuando sientes que vuelves a abandonar" id="volver">
        <p>
          {"Un día llegará. Notarás que llevas 3 días saltándote los 5 minutos. La voz vieja volverá: 'ya estamos otra vez, no lo conseguiré nunca'. Este es el momento crítico. La mayoría de intentos se pierden aquí — no por no hacerlo, sino por la interpretación de ese hecho."}
        </p>
        <p>
          {"Lo que necesitas entonces no es más voluntad. Necesitas un protocolo de recuperación. Aquí tienes uno simple:"}
        </p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#B4C5A4]">
          <li>{"Reconocer sin drama: 'llevo X días sin hacer'."}</li>
          <li>{"Investigar sin castigo: '¿qué ha pasado esta semana?' (viaje, insomnio, estrés?)"}</li>
          <li>{"Bajar el listón: la próxima acción debe ser un 30% de lo que habías hecho hasta ahora."}</li>
          <li>{"Volver mañana. No la semana que viene. Mañana. Con la versión pequeña."}</li>
        </ul>
        <p>
          {"Este protocolo funciona porque saca el moralismo de la ecuación. No estás siendo 'fuerte' o 'débil' — estás gestionando un proyecto a largo plazo que tiene tramos buenos y tramos malos. Los tramos malos forman parte del proyecto, no son su final."}
        </p>
      </Section>

      <Section title="La pregunta que lo cambia todo" id="pregunta">
        <p>
          {"Muchos intentos fallan porque están conectados a una fecha. 'Quiero perder peso antes del verano'. 'Quiero estar mejor para Navidad'. Estas metas tienen un problema: cuando llegan (o cuando es evidente que no llegarás), el proyecto deja de tener sentido."}
        </p>
        <p>
          {"La pregunta que cambia el juego es otra. No es '¿qué quiero conseguir?'. Es '¿quién quiero ser cada día?'. La respuesta es más sencilla de lo que parece. No 'quiero estar delgado'. Sino 'quiero ser alguien que camina cada día un poco'. No 'quiero tener un cuerpo de gimnasio'. Sino 'quiero ser alguien que responde mejor a lo que le pide la vida'. Esta identidad no tiene fecha de caducidad."}
        </p>
        <Pullquote>
          {"Un objetivo se acaba. Una identidad se cuida cada día."}
        </Pullquote>
      </Section>

      <Section title="Un paso concreto que puedes dar hoy" id="paso-hoy">
        <p>
          {"Escribe en un papel el compromiso más pequeño que se te ocurra. Debe ser tan pequeño que te dé vergüenza leerlo en voz alta. Algo como: 'me pondré las zapatillas deportivas el lunes por la mañana'. Nada más. No 'y luego saldré a caminar'. Solo ponerse las zapatillas."}
        </p>
        <p>
          {"Haz eso una semana. Cinco días laborables, ponerte las zapatillas por la mañana. No hace falta caminar. Si caminas, bien. Si no, también bien. Al cabo de la semana, revisa: ¿cuántos días te las has puesto?"}
        </p>
        <p>
          {"Este tipo de compromiso mínimo se aplica especialmente bien cuando empiezas a moverte por primera vez — hay una guía entera sobre "}
          <Link to="/es/blog/como-empezar-a-moverte-con-sobrepeso" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            cómo empezar a moverte si tienes sobrepeso
          </Link>
          {", con el método de los 5 minutos, los tres ejercicios de fuerza básicos y cómo escuchar el dolor sin abandonarlo todo."}
        </p>
        <p>
          {"Y si uno de los motivos que te hace abandonar es sentirte observado, "}
          <Link to="/es/blog/me-da-verguenza-ir-al-gimnasio" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            en este artículo explicamos por qué da vergüenza moverse en público
          </Link>
          {" y qué espacios funcionan mejor mientras construyes confianza."}
        </p>
        <p>
          {"Si quieres entender la relación entre este tipo de compromiso y el verdadero cambio de fondo, puedes leer nuestra guía central sobre "}
          <Link to="/es/blog/como-activarte-cuando-llevas-anos-sin-moverte" className="text-[#0B2545] underline decoration-[#B4C5A4] decoration-2 underline-offset-2 hover:decoration-[#0B2545]">
            cómo activarte cuando llevas años sin moverte
          </Link>
          {". Allí explicamos por qué empezar pequeño es el 90% del cambio y cómo construir una identidad sostenible."}
        </p>
        <p>
          {"Y si quieres que te ayudemos a mapear cuáles son tus 3 puntos de abandono habituales, déjanos tu correo y te haremos llegar un test inicial para identificarlos juntos."}
        </p>
      </Section>
    </>
  );
}
